import { Share2, Copy, Mail, Facebook, Twitter, MessageCircle, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PinIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.93-.19-2.37.04-3.39.2-.9 1.32-5.73 1.32-5.73s-.34-.68-.34-1.68c0-1.58.91-2.76 2.05-2.76.97 0 1.44.73 1.44 1.6 0 .98-.62 2.44-.95 3.8-.27 1.13.57 2.05 1.69 2.05 2.03 0 3.58-2.14 3.58-5.22 0-2.73-1.96-4.64-4.77-4.64-3.25 0-5.16 2.44-5.16 4.96 0 .98.38 2.04.85 2.61a.34.34 0 0 1 .08.33c-.09.36-.29 1.13-.33 1.29-.05.22-.17.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.39 2.96 7.39 6.92 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.35-.63-2.74-1.37l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z"/>
  </svg>
);

export const ShareMenu = ({ title, description = '', url, image = '', buttonVariant = 'outline', buttonClassName = '' }) => {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = `${title}${description ? ` — ${description}` : ''}`;

  const openShare = (href) => {
    window.open(href, '_blank', 'noopener,noreferrer,width=600,height=520');
  };

  const handleFacebook = () => {
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  };
  const handleTwitter = () => {
    openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`);
  };
  const handleWhatsapp = () => {
    openShare(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`);
  };
  const handlePinterest = () => {
    const media = image ? `&media=${encodeURIComponent(image)}` : '';
    openShare(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}${media}&description=${encodeURIComponent(shareText)}`);
  };
  const handleEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié !');
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };
  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url: shareUrl });
      } catch (err) {
        // AbortError = user cancelled, ignore. Other errors should be surfaced.
        if (err && err.name !== 'AbortError') {
          toast.error('Échec du partage');
        }
      }
    } else {
      handleCopy();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={buttonVariant}
          className={`gap-2 ${buttonClassName}`}
          data-testid="share-menu-trigger"
        >
          <Share2 className="w-5 h-5" />
          Partager
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-heading">Partager cette recette</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {typeof navigator !== 'undefined' && navigator.share && (
          <DropdownMenuItem onClick={handleNative} className="gap-2 cursor-pointer" data-testid="share-native">
            <Share2 className="w-4 h-4" /> Partage rapide
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleFacebook} className="gap-2 cursor-pointer" data-testid="share-facebook">
          <Facebook className="w-4 h-4 text-[#1877F2]" /> Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleTwitter} className="gap-2 cursor-pointer" data-testid="share-twitter">
          <Twitter className="w-4 h-4 text-[#1DA1F2]" /> Twitter / X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleWhatsapp} className="gap-2 cursor-pointer" data-testid="share-whatsapp">
          <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handlePinterest} className="gap-2 cursor-pointer" data-testid="share-pinterest">
          <PinIcon className="w-4 h-4 text-[#E60023]" /> Pinterest
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleEmail} className="gap-2 cursor-pointer" data-testid="share-email">
          <Mail className="w-4 h-4 text-worldplate-muted" /> Email
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleCopy} className="gap-2 cursor-pointer" data-testid="share-copy">
          <LinkIcon className="w-4 h-4" /> Copier le lien
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareMenu;
